/**
 * Standards Portal Page — M6.2 Phase 1
 * Public/Internal view of governing standards with applicability and verification status
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  Search, Filter, ExternalLink, CheckCircle2, AlertCircle, Info, XCircle,
  ChevronDown, ChevronRight, Building2, Shield, FileCheck, RefreshCw,
  BookOpen, Loader2, Plus, Archive, Eye
} from 'lucide-react';

// Domain icons mapping
const DOMAIN_ICONS = {
  medical_devices: '🏥',
  software_samd: '💻',
  electrical_safety: '⚡',
  pharma_biotech: '🧪',
  automotive_industrial: '🚗',
  laboratory_quality: '🔬'
};

// Applicability badge component
const ApplicabilityBadge = ({ status, t }) => {
  const configs = {
    applicable: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle2, label: t ? t('governance:standards.applicableBadge') : 'Applicable' },
    partially_applicable: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: AlertCircle, label: t ? t('governance:standards.partiallyApplicableBadge') : 'Partially Applicable' },
    informational: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Info, label: t ? t('governance:standards.informationalBadge') : 'Informational' },
    not_applicable: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle, label: t ? t('governance:standards.notApplicableBadge') : 'Not Applicable' }
  };
  
  const config = configs[status] || configs.informational;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Verification badge component
const VerificationBadge = ({ status, t }) => {
  const configs = {
    verified: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: Shield, label: t ? t('governance:standards.verifiedBadge') : 'Verified' },
    under_review: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: RefreshCw, label: t ? t('governance:standards.underReview') : 'Under Review' },
    draft: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: FileCheck, label: t ? t('governance:standards.draft') : 'Draft' },
    needs_update: { bg: 'bg-orange-500/15', text: 'text-orange-400', icon: AlertCircle, label: t ? t('governance:standards.needsUpdate') : 'Needs Update' },
    archived: { bg: 'bg-red-500/15', text: 'text-red-400', icon: Archive, label: t ? t('governance:standards.archived') : 'Archived' }
  };
  
  const config = configs[status] || configs.draft;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Standard card component
const StandardCard = ({ standard, onViewDetails, isAdmin, t }) => {
  return (
    <div className="qms-card hover:border-[#36A9E1]/30 transition-all cursor-pointer group" onClick={() => onViewDetails(standard)}>
      <div className="qms-card-body p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{DOMAIN_ICONS[standard.domain] || '📋'}</span>
              <h3 className="text-white font-semibold text-sm truncate group-hover:text-[#36A9E1] transition-colors">
                {standard.short_name}
              </h3>
            </div>
            <p className="text-[#8B95A5] text-xs line-clamp-2">{standard.title}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <ApplicabilityBadge status={standard.charstan_applicability} t={t} />
            <VerificationBadge status={standard.verification_status} t={t} />
          </div>
        </div>
        
        <p className="text-[#8B95A5] text-xs line-clamp-2 mb-3">{standard.public_summary}</p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[#8B95A5] text-xs">
              <Building2 size={12} className="inline mr-1" />
              {standard.issuing_body}
            </span>
            {standard.edition && (
              <span className="text-[#8B95A5] text-xs">• {standard.edition}</span>
            )}
          </div>
          {standard.official_url && (
            <a
              href={standard.official_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[#36A9E1] hover:text-[#38BDF8] text-xs flex items-center gap-1 transition-colors"
            >
              <ExternalLink size={12} />
              {t('governance:standards.source')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Standard detail modal
const StandardDetailModal = ({ standard, onClose, isAdmin, t }) => {
  if (!standard) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1A1D24] border border-[rgba(255,255,255,0.07)] rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[rgba(255,255,255,0.07)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{DOMAIN_ICONS[standard.domain] || '📋'}</span>
                <h2 className="text-white font-semibold text-lg">{standard.short_name}</h2>
              </div>
              <p className="text-[#8B95A5] text-sm">{standard.title}</p>
            </div>
            <button onClick={onClose} className="text-[#8B95A5] hover:text-white transition-colors">
              <XCircle size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:standards.issuingBody')}</label>
              <p className="text-white text-sm">{standard.issuing_body}</p>
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:standards.edition')}</label>
              <p className="text-white text-sm">{standard.edition || 'N/A'}</p>
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:standards.charstanApplicability')}</label>
              <ApplicabilityBadge status={standard.charstan_applicability} t={t} />
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:standards.verificationStatus')}</label>
              <VerificationBadge status={standard.verification_status} t={t} />
            </div>
          </div>
          
          <div className="mb-6">
            <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-2">{t('governance:standards.summaryLabel')}</label>
            <p className="text-white text-sm leading-relaxed">{standard.public_summary}</p>
          </div>
          
          {standard.applicability_notes && (
            <div className="mb-6">
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-2">{t('governance:standards.applicabilityNotes')}</label>
              <p className="text-[#8B95A5] text-sm leading-relaxed">{standard.applicability_notes}</p>
            </div>
          )}
          
          {standard.official_url && (
            <div className="mb-6">
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-2">{t('governance:standards.officialSource')}</label>
              <a
                href={standard.official_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#36A9E1] hover:text-[#38BDF8] text-sm flex items-center gap-1 transition-colors"
              >
                <ExternalLink size={14} />
                {standard.official_url}
              </a>
            </div>
          )}
          
          {standard.verified_at && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <Shield size={16} />
                <span className="font-medium text-sm">{t('governance:standards.sourceVerified')}</span>
              </div>
              <p className="text-[#8B95A5] text-xs">
                {t('governance:standards.sourceConfirmed')}
              </p>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
          {standard.official_url && (
            <a
              href={standard.official_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-[#36A9E1] hover:bg-[#36A9E1]/10 rounded transition-colors text-sm flex items-center gap-2"
            >
              <ExternalLink size={14} />
              View Official Source
            </a>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#2D313D] text-white hover:bg-[#3D4150] rounded transition-colors text-sm"
          >
            {t('common:actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Standards Portal Page
const StandardsPortalPage = () => {
  const { t } = useTranslation(['governance', 'common']);
  const { isAdmin, isAuditor } = useAuth();
  
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedApplicability, setSelectedApplicability] = useState('all');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  
  // Detail modal
  const [selectedStandard, setSelectedStandard] = useState(null);
  
  // Expanded domains in grouped view
  const [expandedDomains, setExpandedDomains] = useState({});
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'grouped'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [standardsRes, categoriesRes, statsRes] = await Promise.all([
        fetch(`${API}/governance/standards`, { credentials: 'include' }),
        fetch(`${API}/governance/standards/categories`, { credentials: 'include' }),
        fetch(`${API}/governance/standards/stats`, { credentials: 'include' })
      ]);
      
      if (!standardsRes.ok || !categoriesRes.ok || !statsRes.ok) {
        throw new Error(t('governance:standards.errorLoad'));
      }
      
      const [standards, categories, stats] = await Promise.all([
        standardsRes.json(),
        categoriesRes.json(),
        statsRes.json()
      ]);
      
      setStandards(standards);
      setCategories(categories.categories || []);
      setStats(stats);
      setError(null);
      
      // Expand all domains by default
      const expanded = {};
      (categories.categories || []).forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedDomains(expanded);
    } catch (err) {
      console.error('Error loading standards:', err);
      setError('Failed to load standards registry');
    } finally {
      setLoading(false);
    }
  };

  // Filtered standards
  const filteredStandards = useMemo(() => {
    return standards.filter(std => {
      if (selectedDomain !== 'all' && std.domain !== selectedDomain) return false;
      if (selectedApplicability !== 'all' && std.charstan_applicability !== selectedApplicability) return false;
      if (verifiedOnly && std.verification_status !== 'verified') return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          std.title.toLowerCase().includes(q) ||
          std.short_name.toLowerCase().includes(q) ||
          std.issuing_body.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [standards, selectedDomain, selectedApplicability, verifiedOnly, searchQuery]);

  // Group by domain
  const groupedStandards = useMemo(() => {
    const groups = {};
    filteredStandards.forEach(std => {
      if (!groups[std.domain]) {
        const cat = categories.find(c => c.id === std.domain);
        groups[std.domain] = {
          domain: std.domain,
          name: cat?.name || std.domain,
          icon: cat?.icon || '📋',
          description: cat?.description || '',
          standards: []
        };
      }
      groups[std.domain].standards.push(std);
    });
    return Object.values(groups);
  }, [filteredStandards, categories]);

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => ({ ...prev, [domain]: !prev[domain] }));
  };

  if (loading) {
    return (
      <QMSLayout title={t('governance:standards.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('governance:standards.title')}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="text-[#36A9E1]" size={28} />
          <h1 className="font-['Syne'] text-2xl font-bold text-white">{t('governance:standards.pageTitle')}</h1>
        </div>
        <p className="text-[#8B95A5] text-sm">
          {t('governance:standards.pageDescription')}
        </p>
      </div>

      {/* Regulatory Disclaimer Banner */}
      <div className="qms-card border-amber-500/30 mb-6">
        <div className="qms-card-body p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-amber-400 font-medium text-sm mb-1">{t('governance:standards.disclaimerTitle')}</h4>
              <p className="text-[#8B95A5] text-xs leading-relaxed mb-2">
                {t('governance:standards.disclaimerText1')} <span className="text-white font-medium">{t('governance:standards.disclaimerAlwaysAccess')}</span> {t('governance:standards.disclaimerText1b')}
              </p>
              <p className="text-[#8B95A5] text-xs leading-relaxed">
                {t('governance:standards.disclaimerText2')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:standards.totalStandards')}</div>
            <div className="text-white text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:standards.verified')}</div>
            <div className="text-emerald-400 text-2xl font-bold">{stats.verified}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:standards.applicable')}</div>
            <div className="text-cyan-400 text-2xl font-bold">{stats.applicable}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:standards.partiallyApplicable')}</div>
            <div className="text-amber-400 text-2xl font-bold">{stats.partially_applicable}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:standards.informational')}</div>
            <div className="text-blue-400 text-2xl font-bold">{stats.informational}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="qms-card mb-6">
        <div className="qms-card-body p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" size={16} />
              <input
                type="text"
                placeholder={t('governance:standards.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm placeholder-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
              />
            </div>
            
            {/* Domain Filter */}
            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
            >
              <option value="all">{t('governance:standards.allDomains')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
            
            {/* Applicability Filter */}
            <select
              value={selectedApplicability}
              onChange={(e) => setSelectedApplicability(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
            >
              <option value="all">{t('governance:standards.allApplicability')}</option>
              <option value="applicable">{t('governance:standards.applicableBadge')}</option>
              <option value="partially_applicable">{t('governance:standards.partiallyApplicableBadge')}</option>
              <option value="informational">{t('governance:standards.informationalBadge')}</option>
              <option value="not_applicable">{t('governance:standards.notApplicableBadge')}</option>
            </select>
            
            {/* Verified Only Toggle */}
            <label className="flex items-center gap-2 text-sm text-[#8B95A5] cursor-pointer">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
                className="rounded border-[rgba(255,255,255,0.2)] bg-[#0B0E14] text-[#36A9E1] focus:ring-[#36A9E1]"
              />
              {t('governance:standards.verifiedOnly')}
            </label>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-[#0B0E14] rounded p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 rounded text-xs transition-colors ${viewMode === 'grid' ? 'bg-[#36A9E1] text-white' : 'text-[#8B95A5] hover:text-white'}`}
              >
                {t('governance:standards.grid')}
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`px-3 py-1 rounded text-xs transition-colors ${viewMode === 'grouped' ? 'bg-[#36A9E1] text-white' : 'text-[#8B95A5] hover:text-white'}`}
              >
                {t('governance:standards.grouped')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="qms-card border-red-500/30 mb-6">
          <div className="qms-card-body p-4 text-red-400 text-sm">{error}</div>
        </div>
      )}

      {/* Standards Display */}
      {viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStandards.map(std => (
            <StandardCard
              key={std.standard_id}
              standard={std}
              onViewDetails={setSelectedStandard}
              isAdmin={isAdmin}
              t={t}
            />
          ))}
        </div>
      ) : (
        /* Grouped View */
        <div className="space-y-4">
          {groupedStandards.map(group => (
            <div key={group.domain} className="qms-card">
              <div
                className="qms-card-header flex items-center justify-between cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                onClick={() => toggleDomain(group.domain)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{group.icon}</span>
                  <div>
                    <h3 className="text-white font-semibold">{group.name}</h3>
                    <p className="text-[#8B95A5] text-xs">{group.standards.length} {t('governance:standards.standardsCount')}</p>
                  </div>
                </div>
                {expandedDomains[group.domain] ? (
                  <ChevronDown className="text-[#8B95A5]" size={20} />
                ) : (
                  <ChevronRight className="text-[#8B95A5]" size={20} />
                )}
              </div>
              
              {expandedDomains[group.domain] && (
                <div className="qms-card-body p-4">
                  <p className="text-[#8B95A5] text-sm mb-4">{group.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {group.standards.map(std => (
                      <div
                        key={std.standard_id}
                        className="bg-[#0B0E14] border border-[rgba(255,255,255,0.05)] rounded-lg p-3 hover:border-[#36A9E1]/30 cursor-pointer transition-colors"
                        onClick={() => setSelectedStandard(std)}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-white font-medium text-sm">{std.short_name}</h4>
                          <ApplicabilityBadge status={std.charstan_applicability} t={t} />
                        </div>
                        <p className="text-[#8B95A5] text-xs line-clamp-2">{std.public_summary}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[#8B95A5] text-xs">{std.issuing_body}</span>
                          <VerificationBadge status={std.verification_status} t={t} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredStandards.length === 0 && !loading && (
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <BookOpen className="mx-auto mb-4 text-[#8B95A5]" size={48} />
            <h3 className="text-white font-semibold mb-2">{t('governance:standards.noStandards')}</h3>
            <p className="text-[#8B95A5] text-sm">
              {searchQuery ? t('governance:standards.noStandardsSearch') : t('governance:standards.noStandardsMatch')}
            </p>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedStandard && (
        <StandardDetailModal
          standard={selectedStandard}
          onClose={() => setSelectedStandard(null)}
          isAdmin={isAdmin}
          t={t}
        />
      )}
    </QMSLayout>
  );
};

export default StandardsPortalPage;
